import React from 'react';
import { X, Info, AlertTriangle, CheckCircle, Activity, FileText, BookOpen } from 'lucide-react';

interface GuideData {
  title: Record<string, string>;
  aim: Record<string, string>;
  scoring: Record<string, { range: string; meaning: string; color: string }[]>;
  guidelines: Record<string, string[]>;
}

const guides: Record<string, GuideData> = {
  salt: {
    title: { en: 'SALT Clinical Guide', ar: 'الدليل السريري لمقياس SALT' },
    aim: {
      en: 'Severity of Alopecia Tool to measure the percentage of hair loss on the scalp in Alopecia Areata.',
      ar: 'مقياس لتقييم شدة ومساحة تساقط الشعر في فروة الرأس لمرضى الثعلبة البقعية (Alopecia Areata).'
    },
    scoring: {
      en: [
        { range: 'S0', meaning: 'No hair loss (0%)', color: 'bg-green-100 text-green-800' },
        { range: 'S1', meaning: 'Mild (<25%)', color: 'bg-blue-100 text-blue-800' },
        { range: 'S2', meaning: 'Moderate (25-49%)', color: 'bg-yellow-100 text-yellow-800' },
        { range: 'S3', meaning: 'Severe (50-74%)', color: 'bg-orange-100 text-orange-800' },
        { range: 'S4', meaning: 'Very Severe (75-99%)', color: 'bg-red-100 text-red-800' },
        { range: 'S5', meaning: 'Alopecia Totalis (100%)', color: 'bg-red-200 text-red-900' }
      ],
      ar: [
        { range: 'S0', meaning: 'لا يوجد تساقط (0%)', color: 'bg-green-100 text-green-800' },
        { range: 'S1', meaning: 'خفيف (أقل من 25%)', color: 'bg-blue-100 text-blue-800' },
        { range: 'S2', meaning: 'متوسط (25-49%)', color: 'bg-yellow-100 text-yellow-800' },
        { range: 'S3', meaning: 'شديد (50-74%)', color: 'bg-orange-100 text-orange-800' },
        { range: 'S4', meaning: 'شديد جداً (75-99%)', color: 'bg-red-100 text-red-800' },
        { range: 'S5', meaning: 'ثعلبة كاملة الرأس (100%)', color: 'bg-red-200 text-red-900' }
      ]
    },
    guidelines: {
      en: [
        'S1-S2 (<50%): Usually treated with intralesional corticosteroids or topical therapies.',
        'S3-S5 (≥50%): Often requires systemic therapy (e.g., JAK inhibitors, oral corticosteroids, contact immunotherapy).'
      ],
      ar: [
        'درجة S1-S2 (أقل من 50%): تُعالج عادة بالحقن الموضعي للكورتيزون أو العلاجات الموضعية.',
        'درجة S3-S5 (50% فأكثر): تتطلب غالباً علاجاً جهازياً (مثل مثبطات JAK، الكورتيزون الفموي، أو العلاج المناعي).'
      ]
    }
  },
  easi: {
    title: { en: 'EASI Clinical Guide', ar: 'الدليل السريري لمقياس EASI' },
    aim: {
      en: 'Validated tool used to measure the extent and severity of atopic dermatitis (eczema).',
      ar: 'أداة معتمدة لقياس مدى انتشار وشدة التهاب الجلد التأتبي (الإكزيما).'
    },
    scoring: {
      en: [
        { range: '0', meaning: 'Clear', color: 'bg-green-100 text-green-800' },
        { range: '0.1-1.0', meaning: 'Almost Clear', color: 'bg-green-100 text-green-800' },
        { range: '1.1-7.0', meaning: 'Mild Eczema', color: 'bg-blue-100 text-blue-800' },
        { range: '7.1-21.0', meaning: 'Moderate Eczema', color: 'bg-yellow-100 text-yellow-800' },
        { range: '21.1-50.0', meaning: 'Severe Eczema', color: 'bg-orange-100 text-orange-800' },
        { range: '50.1-72.0', meaning: 'Very Severe Eczema', color: 'bg-red-100 text-red-800' }
      ],
      ar: [
        { range: '0', meaning: 'سليم تماماً', color: 'bg-green-100 text-green-800' },
        { range: '0.1-1.0', meaning: 'شبه سليم', color: 'bg-green-100 text-green-800' },
        { range: '1.1-7.0', meaning: 'إكزيما خفيفة', color: 'bg-blue-100 text-blue-800' },
        { range: '7.1-21.0', meaning: 'إكزيما متوسطة', color: 'bg-yellow-100 text-yellow-800' },
        { range: '21.1-50.0', meaning: 'إكزيما شديدة', color: 'bg-orange-100 text-orange-800' },
        { range: '50.1-72.0', meaning: 'إكزيما شديدة جداً', color: 'bg-red-100 text-red-800' }
      ]
    },
    guidelines: {
      en: [
        'EASI > 21 usually indicates severe disease requiring systemic therapy or biologic agents (e.g. Dupilumab).',
        'EASI 50, 75, or 90 indicates a 50%, 75%, or 90% reduction in EASI score, widely used as clinical endpoints.'
      ],
      ar: [
        'درجة > 21 تشير عادة إلى مرض شديد يتطلب علاجاً جهازياً (Systemic therapy) أو بيولوجياً (مثل الدوبيلوماب).',
        'يستخدم EASI 50 أو 75 كمؤشر رئيسي لتقييم مدى فعالية واستجابة المريض للعلاج.'
      ]
    }
  },
  pasi: {
    title: { en: 'PASI Clinical Guide', ar: 'الدليل السريري لمقياس PASI' },
    aim: {
      en: 'Gold standard tool for assessing the severity and extent of psoriasis.',
      ar: 'المعيار الذهبي لتقييم شدة ومدى انتشار مرض الصدفية.'
    },
    scoring: {
      en: [
        { range: '0-4', meaning: 'Mild Psoriasis', color: 'bg-green-100 text-green-800' },
        { range: '5-10', meaning: 'Moderate Psoriasis', color: 'bg-yellow-100 text-yellow-800' },
        { range: '>10', meaning: 'Severe Psoriasis', color: 'bg-red-100 text-red-800' }
      ],
      ar: [
        { range: '0-4', meaning: 'صدفية خفيفة', color: 'bg-green-100 text-green-800' },
        { range: '5-10', meaning: 'صدفية متوسطة', color: 'bg-yellow-100 text-yellow-800' },
        { range: '>10', meaning: 'صدفية شديدة', color: 'bg-red-100 text-red-800' }
      ]
    },
    guidelines: {
      en: [
        'PASI > 10 usually indicates severe disease requiring systemic therapy or biologic agents.',
        'PASI 75 (75% reduction in PASI score) is the standard measure for treatment efficacy in clinical trials.'
      ],
      ar: [
        'درجة > 10 تشير عادة إلى مرض شديد يتطلب علاجاً جهازياً (Systemic therapy) أو بيولوجياً.',
        'تحقيق PASI 75 (انخفاض الدرجة بنسبة 75%) هو المقياس القياسي لفعالية العلاج.'
      ]
    }
  },
  dlqi: {
    title: { en: 'DLQI Clinical Guide', ar: 'الدليل السريري لمقياس DLQI' },
    aim: {
      en: 'Measures how much a skin problem has affected the patient\'s life over the last week.',
      ar: 'يقيس مدى تأثير المشكلة الجلدية على حياة المريض خلال الأسبوع الماضي.'
    },
    scoring: {
      en: [
        { range: '0-1', meaning: 'No effect at all', color: 'bg-green-100 text-green-800' },
        { range: '2-5', meaning: 'Small effect', color: 'bg-blue-100 text-blue-800' },
        { range: '6-10', meaning: 'Moderate effect', color: 'bg-yellow-100 text-yellow-800' },
        { range: '11-20', meaning: 'Very large effect', color: 'bg-orange-100 text-orange-800' },
        { range: '21-30', meaning: 'Extremely large effect', color: 'bg-red-100 text-red-800' }
      ],
      ar: [
        { range: '0-1', meaning: 'ليس له تأثير', color: 'bg-green-100 text-green-800' },
        { range: '2-5', meaning: 'تأثير بسيط', color: 'bg-blue-100 text-blue-800' },
        { range: '6-10', meaning: 'تأثير متوسط', color: 'bg-yellow-100 text-yellow-800' },
        { range: '11-20', meaning: 'تأثير كبير جداً', color: 'bg-orange-100 text-orange-800' },
        { range: '21-30', meaning: 'تأثير شديد للغاية', color: 'bg-red-100 text-red-800' }
      ]
    },
    guidelines: {
      en: [
        'A score > 10 usually indicates severe disease requiring active intervention (e.g., systemic therapy or biologics).',
        'Used to monitor treatment response; a decrease of at least 4 points is considered clinically meaningful.'
      ],
      ar: [
        'درجة > 10 تشير عادة إلى مرض شديد يتطلب تدخلاً علاجياً فعالاً (مثل العلاج الجهازي أو البيولوجي).',
        'يُستخدم لمراقبة الاستجابة للعلاج؛ انخفاض الدرجة بمقدار 4 نقاط على الأقل يعتبر استجابة سريرية جيدة.'
      ]
    }
  },
  phq9: {
    title: { en: 'PHQ-9 Clinical Guide', ar: 'الدليل السريري لمقياس PHQ-9' },
    aim: {
      en: 'Screens, diagnoses, monitors, and measures the severity of depression.',
      ar: 'يستخدم للفرز والتشخيص والمتابعة وقياس شدة الاكتئاب.'
    },
    scoring: {
      en: [
        { range: '0-4', meaning: 'Minimal/None', color: 'bg-green-100 text-green-800' },
        { range: '5-9', meaning: 'Mild', color: 'bg-blue-100 text-blue-800' },
        { range: '10-14', meaning: 'Moderate', color: 'bg-yellow-100 text-yellow-800' },
        { range: '15-19', meaning: 'Moderately Severe', color: 'bg-orange-100 text-orange-800' },
        { range: '20-27', meaning: 'Severe', color: 'bg-red-100 text-red-800' }
      ],
      ar: [
        { range: '0-4', meaning: 'طبيعي / لا يوجد', color: 'bg-green-100 text-green-800' },
        { range: '5-9', meaning: 'خفيف', color: 'bg-blue-100 text-blue-800' },
        { range: '10-14', meaning: 'متوسط', color: 'bg-yellow-100 text-yellow-800' },
        { range: '15-19', meaning: 'متوسط إلى شديد', color: 'bg-orange-100 text-orange-800' },
        { range: '20-27', meaning: 'شديد', color: 'bg-red-100 text-red-800' }
      ]
    },
    guidelines: {
      en: [
        'Scores 5-9: Watchful waiting; repeat at follow-up.',
        'Scores 10-14: Consider counseling, follow-up, or pharmacotherapy.',
        'Scores 15-19: Active treatment with pharmacotherapy and/or psychotherapy.',
        'Scores 20-27: Immediate initiation of pharmacotherapy; if severe, refer to mental health specialist.',
        'Note: Always assess for suicide risk if Question 9 is > 0.'
      ],
      ar: [
        'الدرجات 5-9: المراقبة والانتظار (Watchful waiting) والمتابعة.',
        'الدرجات 10-14: يُنصح بالتوجيه النفسي والمتابعة، أو العلاج الدوائي.',
        'الدرجات 15-19: بدء العلاج الدوائي و/أو العلاج النفسي (Psychotherapy).',
        'الدرجات 20-27: بدء العلاج الدوائي فوراً، وفي الحالات الشديدة يُحال لطبيب نفسي.',
        'ملاحظة: يجب دائماً تقييم خطر الانتحار إذا كانت إجابة السؤال التاسع > 0.'
      ]
    }
  },
  gad7: {
    title: { en: 'GAD-7 Clinical Guide', ar: 'الدليل السريري لمقياس GAD-7' },
    aim: {
      en: 'Screening tool and severity measure for generalized anxiety disorder.',
      ar: 'أداة فحص ومقياس لشدة اضطراب القلق العام.'
    },
    scoring: {
      en: [
        { range: '0-4', meaning: 'Minimal Anxiety', color: 'bg-green-100 text-green-800' },
        { range: '5-9', meaning: 'Mild Anxiety', color: 'bg-blue-100 text-blue-800' },
        { range: '10-14', meaning: 'Moderate Anxiety', color: 'bg-yellow-100 text-yellow-800' },
        { range: '15-21', meaning: 'Severe Anxiety', color: 'bg-red-100 text-red-800' }
      ],
      ar: [
        { range: '0-4', meaning: 'قلق طبيعي / لا يوجد', color: 'bg-green-100 text-green-800' },
        { range: '5-9', meaning: 'قلق خفيف', color: 'bg-blue-100 text-blue-800' },
        { range: '10-14', meaning: 'قلق متوسط', color: 'bg-yellow-100 text-yellow-800' },
        { range: '15-21', meaning: 'قلق شديد', color: 'bg-red-100 text-red-800' }
      ]
    },
    guidelines: {
      en: [
        'Scores 5-9: Watchful waiting; no immediate treatment indicated.',
        'Scores 10-14: Clinically significant anxiety; further evaluation required. Consider counseling or medication.',
        'Scores 15-21: Active treatment with pharmacotherapy and/or psychotherapy.'
      ],
      ar: [
        'الدرجات 5-9: المراقبة والانتظار؛ لا يوجد تدخل علاجي فوري مطلوب.',
        'الدرجات 10-14: قلق سريري ملحوظ؛ يتطلب تقييم إضافي، يُنصح بالعلاج النفسي أو الدوائي.',
        'الدرجات 15-21: يتطلب بدء العلاج الدوائي و/أو العلاج النفسي بشكل فعال.'
      ]
    }
  },

  basdai: {
    title: { en: 'BASDAI Clinical Guide', ar: 'الدليل السريري لمقياس BASDAI' },
    aim: {
      en: 'A validated diagnostic instrument used to assess disease activity in patients with Ankylosing Spondylitis (AS).',
      ar: 'أداة معتمدة لتقييم نشاط المرض لدى مرضى التهاب الفقار المقسط (Ankylosing Spondylitis).'
    },
    scoring: {
      en: [
        { range: '< 4', meaning: 'Good Control', color: 'bg-green-100 text-green-800' },
        { range: '≥ 4', meaning: 'Suboptimal Control (Active Disease)', color: 'bg-red-100 text-red-800' }
      ],
      ar: [
        { range: 'أقل من 4', meaning: 'سيطرة جيدة على المرض', color: 'bg-green-100 text-green-800' },
        { range: '4 فأكثر', meaning: 'سيطرة غير كافية (مرض نشط)', color: 'bg-red-100 text-red-800' }
      ]
    },
    guidelines: {
      en: [
        'A score of 4 or greater typically indicates active disease and may qualify the patient for biological therapy (e.g., TNF inhibitors) according to ASAS guidelines.',
        'A clinically important improvement is defined as a reduction of ≥ 2 units or 50% improvement.'
      ],
      ar: [
        'درجة 4 فأكثر تشير عادة إلى نشاط عالٍ للمرض وقد تؤهل المريض لبدء العلاج البيولوجي (مثل مثبطات TNF) وفقاً لإرشادات ASAS.',
        'التحسن السريري الملحوظ يُعرّف بانخفاض النتيجة بمقدار وحدتين على الأقل أو تحسن بنسبة 50%.'
      ]
    }
  },
  poem: {
    title: { en: 'POEM Clinical Guide', ar: 'الدليل السريري لمقياس POEM' },
    aim: {
      en: 'Patient-Oriented Eczema Measure: A tool to monitor atopic eczema severity from the patient\'s perspective over the last 7 days.',
      ar: 'أداة لتقييم شدة الإكزيما (التهاب الجلد التأتبي) بناءً على تجربة المريض خلال الأيام السبعة الماضية.'
    },
    scoring: {
      en: [
        { range: '0 - 2', meaning: 'Clear or almost clear', color: 'bg-green-100 text-green-800' },
        { range: '3 - 7', meaning: 'Mild eczema', color: 'bg-blue-100 text-blue-800' },
        { range: '8 - 16', meaning: 'Moderate eczema', color: 'bg-yellow-100 text-yellow-800' },
        { range: '17 - 24', meaning: 'Severe eczema', color: 'bg-orange-100 text-orange-800' },
        { range: '25 - 28', meaning: 'Very severe eczema', color: 'bg-red-100 text-red-800' }
      ],
      ar: [
        { range: '0 - 2', meaning: 'صافي أو شبه صافي', color: 'bg-green-100 text-green-800' },
        { range: '3 - 7', meaning: 'إكزيما خفيفة', color: 'bg-blue-100 text-blue-800' },
        { range: '8 - 16', meaning: 'إكزيما متوسطة', color: 'bg-yellow-100 text-yellow-800' },
        { range: '17 - 24', meaning: 'إكزيما شديدة', color: 'bg-orange-100 text-orange-800' },
        { range: '25 - 28', meaning: 'إكزيما شديدة جداً', color: 'bg-red-100 text-red-800' }
      ]
    },
    guidelines: {
      en: [
        'A change of 3.4 points or more is considered clinically important.',
        'POEM is often used alongside physician-assessed tools like EASI to capture the subjective patient burden (sleep, itch, bleeding).'
      ],
      ar: [
        'أي تغير في النتيجة بمقدار 3.4 نقاط أو أكثر يُعتبر تحسناً أو تدهوراً سريرياً مهماً.',
        'يُستخدم مقياس POEM غالباً جنباً إلى جنب مع المقاييس التي يحددها الطبيب (مثل EASI) لضمان توثيق معاناة المريض الشخصية (كالحكة واضطراب النوم).'
      ]
    }
  },
  mdq: {
    title: { en: 'MDQ Clinical Guide', ar: 'الدليل السريري لمقياس MDQ' },
    aim: {
      en: 'A screening tool to identify patients most likely to have bipolar disorder.',
      ar: 'أداة مسحية للكشف المبدئي عن اضطراب ثنائي القطب (Bipolar Disorder).'
    },
    scoring: {
      en: [
        { range: 'Negative Screen', meaning: 'Bipolar disorder less likely', color: 'bg-green-100 text-green-800' },
        { range: 'Positive Screen', meaning: 'Bipolar disorder likely', color: 'bg-red-100 text-red-800' }
      ],
      ar: [
        { range: 'نتيجة سلبية', meaning: 'احتمال ضعيف للاضطراب ثنائي القطب', color: 'bg-green-100 text-green-800' },
        { range: 'نتيجة إيجابية', meaning: 'احتمال قوي للاضطراب ثنائي القطب', color: 'bg-red-100 text-red-800' }
      ]
    },
    guidelines: {
      en: [
        'A positive screen requires: YES to ≥ 7 items in Question 1, AND YES to Question 2, AND Moderate/Serious problem in Question 3.',
        'A positive screen requires a comprehensive psychiatric evaluation to confirm a Bipolar I or II diagnosis.',
        'NOTE: This is a screening tool, not diagnostic. False positives may occur in borderline personality disorder or severe ADHD.'
      ],
      ar: [
        'النتيجة الإيجابية تتطلب: الإجابة "نعم" على 7 فقرات أو أكثر في السؤال الأول + الإجابة "نعم" للسؤال الثاني + وجود "مشكلة متوسطة أو خطيرة" في السؤال الثالث.',
        'النتيجة الإيجابية تتطلب تقييماً نفسياً شاملاً لتأكيد تشخيص الاضطراب ثنائي القطب من النوع الأول أو الثاني.',
        'ملاحظة: هذه الأداة مسحية وليست تشخيصية. قد تظهر نتائج إيجابية خاطئة لدى مرضى اضطراب الشخصية الحدية أو اضطراب فرط الحركة الشديد (ADHD).'
      ]
    }
  },
  hads: {
    title: { en: 'HADS Clinical Guide', ar: 'الدليل السريري لمقياس HADS' },
    aim: {
      en: 'Determines the levels of anxiety (HADS-A) and depression (HADS-D) that a patient is experiencing.',
      ar: 'يحدد مستويات القلق والاكتئاب التي يعاني منها المريض (مستقلة عن الأعراض الجسدية).'
    },
    scoring: {
      en: [
        { range: '0-7', meaning: 'Normal', color: 'bg-green-100 text-green-800' },
        { range: '8-10', meaning: 'Borderline abnormal (Probable Case)', color: 'bg-yellow-100 text-yellow-800' },
        { range: '11-21', meaning: 'Abnormal (Definite Case)', color: 'bg-red-100 text-red-800' }
      ],
      ar: [
        { range: '0-7', meaning: 'طبيعي', color: 'bg-green-100 text-green-800' },
        { range: '8-10', meaning: 'حالة حدّية (احتمال وجود مشكلة)', color: 'bg-yellow-100 text-yellow-800' },
        { range: '11-21', meaning: 'غير طبيعي (حالة مؤكدة)', color: 'bg-red-100 text-red-800' }
      ]
    },
    guidelines: {
      en: [
        'The score is calculated separately for Anxiety (7 questions) and Depression (7 questions).',
        'Scores 8-10: Suggests the presence of the respective state. Requires clinical monitoring.',
        'Scores ≥ 11: High probability of clinical anxiety or depression. Requires active intervention and possible psychiatric referral.'
      ],
      ar: [
        'تُحسب الدرجة بشكل منفصل للقلق (7 أسئلة) والاكتئاب (7 أسئلة).',
        'الدرجات 8-10: تشير إلى احتمالية وجود قلق أو اكتئاب وتحتاج لمتابعة وتقييم سريري.',
        'الدرجات ≥ 11: احتمالية عالية جداً لوجود قلق أو اكتئاب سريري ويتطلب تدخلاً علاجياً فورياً وتحويلاً لطبيب مختص.'
      ]
    }
  }
};

interface CalculatorGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  calculatorType: string;
  lang: 'en' | 'ar';
}

const CalculatorGuideModal: React.FC<CalculatorGuideModalProps> = ({ isOpen, onClose, calculatorType, lang }) => {
  if (!isOpen) return null;

  const guide = guides[calculatorType];
  const isAr = lang === 'ar';

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-[60]" onClick={onClose} dir={isAr ? 'rtl' : 'ltr'}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-100 sticky top-0 bg-white/80 backdrop-blur-md z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
              <BookOpen className="w-5 h-5" />
            </div>
            <h2 className="text-xl font-bold text-slate-800">{guide.title[lang]}</h2>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-xl transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          
          {/* Aim section */}
          <section>
            <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
              <Info className="w-5 h-5 text-indigo-500" />
              {isAr ? 'الهدف من الحاسبة' : 'Purpose'}
            </h3>
            <p className="text-slate-600 bg-slate-50 p-4 rounded-xl leading-relaxed">
              {guide.aim[lang]}
            </p>
          </section>

          {/* Scoring Interpretation */}
          <section>
            <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-indigo-500" />
              {isAr ? 'تفسير الدرجات (Scoring)' : 'Score Interpretation'}
            </h3>
            <div className="grid gap-3">
              {guide.scoring[lang].map((score, idx) => (
                <div key={idx} className="flex items-center gap-4 p-3 rounded-xl border border-slate-100 hover:border-indigo-100 hover:bg-slate-50 transition">
                  <div className={`px-3 py-1 rounded-lg font-bold text-sm shrink-0 ${score.color}`}>
                    {score.range}
                  </div>
                  <div className="text-slate-700 font-medium">
                    {score.meaning}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Guidelines */}
          <section>
            <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-indigo-500" />
              {isAr ? 'التوصيات السريرية (Clinical Actions)' : 'Clinical Recommendations'}
            </h3>
            <ul className="space-y-3 bg-indigo-50/50 p-5 rounded-xl border border-indigo-100/50">
              {guide.guidelines[lang].map((point, idx) => (
                <li key={idx} className="flex gap-3 text-slate-700">
                  <div className="shrink-0 mt-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                  </div>
                  <span className="leading-relaxed">{point}</span>
                </li>
              ))}
            </ul>
          </section>

        </div>
      </div>
    </div>
  );
};

export default CalculatorGuideModal;
